import { useQuery } from '@tanstack/react-query'
import { type ContentAssignmentDTO, ContentScopeType } from '@today/types'
import type { AssignmentListViewProps, AttachDialogViewProps } from '@today/ui-domain'
import { contentService } from '@/services/content.service'
import { sortAssignments } from './assignment-rules'
import { useAssignmentList, useAttachDialog } from './useAssignments'
import { contentKeys, useContentT, useContentViewer } from './useContentQueries'

/** `/content/tenant-defaults` (TO-420 content-admin-ui REQ-012): tenant-scope assignments, attach, detach, reorder. */
export function useTenantDefaults(): {
	list: AssignmentListViewProps
	attach: AttachDialogViewProps
	onAttach?: () => void
} {
	const { t } = useContentT()
	const { caps } = useContentViewer()
	const queryKey = contentKeys.tenantAssignments
	const query = useQuery({
		queryKey,
		queryFn: async () =>
			sortAssignments(await contentService.allAssignments({ 'filters[scopeType]': ContentScopeType.TENANT })),
	})
	const rows = query.data ?? []
	const list = useAssignmentList({
		label: t('tenantDefaults.list'),
		source: rows,
		queryKey,
		write: (_data, ordered: ContentAssignmentDTO[]) => ordered,
		product: false,
		rows: rows.map((a) => ({
			id: a.id,
			textId: a.textId,
			code: a.textCode,
			name: a.textName,
			placement: null,
			replacesTextId: null,
			replacesCode: null,
			chips: [],
		})),
	})
	const { open, ...attach } = useAttachDialog({ scope: { type: ContentScopeType.TENANT }, existing: rows })
	return { list, attach, onAttach: caps.assign ? open : undefined }
}
